import { useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Calendar, Clock, MapPin, User, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const tripTypes = [
  "Round Trip",
  "Package Trip",
  "Airport Transfer Trip",
  "Outstation Trip"
];

const vehicleTypes = [
  "Sedan 4+1",
  "Suv 6+1",
  "Muv 7+1",
  "TT 17+1",
  "Sml 28+1"
];

const passengerOptions = ["2", "4", "6", "7", "14", "17", "22", "28", "35"];

const BookingForm = ({ className = " ", minimal = false, idPrefix = "bf" }: { className?: string, minimal?: boolean, idPrefix?: string }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [pickup, setPickup] = useState("");
  const [tripType, setTripType] = useState("Airport Transfer Trip");
  const [vehicleType, setVehicleType] = useState("Sedan 4+1");
  const [passengers, setPassengers] = useState("2");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  
  const formId = `${idPrefix}-`;

  const [activeMenu, setActiveMenu] = useState<"trip" | "vehicle" | "passengers" | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const msg = `Hi, I want to book a taxi.\n\n👤 Name: ${name}\n📞 Phone: ${phone}\n📍 Pickup: ${pickup}\n🚕 Trip Type: ${tripType}\n🚗 Vehicle: ${vehicleType}\n👥 Passengers: ${passengers}\n📅 Date: ${date}\n🕒 Time: ${time}`;
    window.open(`https://wa.me/919952493907?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className={`relative ${className}`}>
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className={`${minimal ? "bg-transparent p-0 border-0 shadow-none" : "bg-white rounded-[1.5rem] sm:rounded-[2rem] p-4 sm:p-6 shadow-[0_10px_40px_rgba(0,0,0,0.1)] border border-gray-100"} w-full`}
      >
        <div className={`space-y-${minimal ? "2" : "3"} sm:space-y-${minimal ? "2.5" : "4"}`}>
          {/* Name Field */}
          <div>
            <label htmlFor={`${formId}name`} className={`block text-sm font-bold text-gray-800 ${minimal ? "mb-1" : "mb-1.5"} ml-1`}>Name</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className={`w-full bg-white border border-gray-200 rounded-xl ${minimal ? "py-2 px-3" : "py-3 px-4"} text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-green-600 transition-colors`}
                id={`${formId}name`}
              />
            </div>
          </div>

          <div className={minimal ? "grid grid-cols-2 gap-3" : "space-y-4"}>
            {/* Phone Number Field */}
            <div className={minimal ? "" : ""}>
              <label htmlFor={`${formId}phone`} className={`block text-sm font-bold text-gray-800 ${minimal ? "mb-1" : "mb-1.5"} ml-1`}>Phone</label>
              <div className="relative">
                <input
                  type="tel"
                  placeholder="Phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  className={`w-full bg-white border border-gray-200 rounded-xl ${minimal ? "py-2 px-3" : "py-3 px-4"} text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-green-600 transition-colors`}
                  id={`${formId}phone`}
                />
              </div>
            </div>

            {/* Pickup Location Field */}
            <div className={minimal ? "" : ""}>
              <label htmlFor={`${formId}pickup`} className={`block text-sm font-bold text-gray-800 ${minimal ? "mb-1" : "mb-1.5"} ml-1`}>Pickup</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Pickup"
                  value={pickup}
                  onChange={(e) => setPickup(e.target.value)}
                  required
                  className={`w-full bg-white border border-gray-200 rounded-xl ${minimal ? "py-2 px-3" : "py-3 px-4"} text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-green-600 transition-colors`}
                  id={`${formId}pickup`}
                />
              </div>
            </div>
          </div>

          <div className={minimal ? "grid grid-cols-2 gap-3" : "space-y-4"}>
            {/* Trip Type Field */}
            <div>
              <label htmlFor={`${formId}trip-type-button`} className={`block text-sm font-bold text-gray-800 ${minimal ? "mb-1" : "mb-1.5"} ml-1`}>Trip Type</label>
              <button
                type="button"
                onClick={() => setActiveMenu("trip")}
                className={`w-full bg-white border border-gray-200 rounded-xl ${minimal ? "py-2 px-3" : "py-3 px-4"} text-left text-gray-800 flex items-center justify-between hover:border-[#2b8a00] focus:border-[#2b8a00] focus:outline-none transition-colors overflow-hidden ${minimal ? "min-h-[42px]" : "min-h-[52px]"}`}
                id={`${formId}trip-type-button`}
              >
                <span className={`truncate mr-2 ${minimal ? "text-xs" : ""} ${tripType ? "text-gray-800" : "text-gray-400"}`}>
                  {tripType || "Select Trip"}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
              </button>
            </div>

            {/* Type Of Vehicle Field */}
            <div>
              <label htmlFor={`${formId}vehicle-type-button`} className={`block text-sm font-bold text-gray-800 ${minimal ? "mb-1" : "mb-1.5"} ml-1`}>Vehicle</label>
              <button
                type="button"
                onClick={() => setActiveMenu("vehicle")}
                className={`w-full bg-white border border-gray-200 rounded-xl ${minimal ? "py-2 px-3" : "py-3 px-4"} text-left text-gray-800 flex items-center justify-between hover:border-[#2b8a00] focus:border-[#2b8a00] focus:outline-none transition-colors overflow-hidden ${minimal ? "min-h-[42px]" : "min-h-[52px]"}`}
                id={`${formId}vehicle-type-button`}
              >
                <span className={`truncate mr-2 ${minimal ? "text-xs" : ""} ${vehicleType ? "text-gray-800" : "text-gray-400"}`}>
                  {vehicleType || "Select Car"}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
              </button>
            </div>
          </div>

          <div className={minimal ? "grid grid-cols-3 gap-2" : "space-y-4"}>
            {/* Number Of Passengers Field */}
            <div>
              <label htmlFor={`${formId}passengers-button`} className={`block text-sm font-bold text-gray-800 ${minimal ? "mb-1" : "mb-1.5"} ml-1`}>Pax</label>
              <button
                type="button"
                onClick={() => setActiveMenu("passengers")}
                className={`w-full bg-white border border-gray-200 rounded-xl ${minimal ? "py-2 px-2" : "py-3 px-4"} text-left text-gray-800 flex items-center justify-between hover:border-[#2b8a00] focus:border-[#2b8a00] focus:outline-none transition-colors overflow-hidden ${minimal ? "min-h-[42px]" : "min-h-[52px]"}`}
                id={`${formId}passengers-button`}
              >
                <span className={`truncate mr-1 ${minimal ? "text-[10px]" : ""} ${passengers ? "text-gray-800" : "text-gray-400"}`}>
                  {passengers}
                </span>
                <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />
              </button>
            </div>

            {/* Pickup Date Field */}
            <div>
              <label htmlFor={`${formId}pickup-date-input`} className={`block text-sm font-bold text-gray-800 ${minimal ? "mb-1" : "mb-1.5"} ml-1`}>Date</label>
              <div className="relative">
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                  className={`w-full bg-white border border-gray-200 rounded-xl ${minimal ? "py-2 px-1" : "py-3 px-4"} text-gray-800 focus:outline-none focus:border-green-600 transition-colors appearance-none ${minimal ? "text-[10px]" : ""}`}
                  style={{ colorScheme: "light" }}
                  id={`${formId}pickup-date-input`}
                />
              </div>
            </div>

            {/* Pickup Time Field */}
            <div>
              <label htmlFor={`${formId}pickup-time-input`} className={`block text-sm font-bold text-gray-800 ${minimal ? "mb-1" : "mb-1.5"} ml-1`}>Time</label>
              <div className="relative">
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  required
                  className={`w-full bg-white border border-gray-200 rounded-xl ${minimal ? "py-2 px-1" : "py-3 px-4"} text-gray-800 focus:outline-none focus:border-green-600 transition-colors appearance-none ${minimal ? "text-[10px]" : ""}`}
                  style={{ colorScheme: "light" }}
                  id={`${formId}pickup-time-input`}
                />
              </div>
            </div>
          </div>

          {/* Book Now Button */}
          <button
            type="submit"
            className="w-full bg-[#2b8a00] hover:bg-[#1e6000] text-white font-bold py-4 rounded-xl shadow-lg transition-colors mt-4 text-lg"
            id={`${formId}book-now-button`}
          >
            Book Now
          </button>
        </div>
      </motion.form>

      {/* Choice Menus (Overlays) */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {activeMenu && (
            <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setActiveMenu(null)}
                className="absolute inset-0 bg-black/40 backdrop-blur-[2px] cursor-pointer"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="relative w-full max-w-[320px] bg-white rounded-3xl shadow-2xl z-[9999] overflow-hidden"
              >
                <div className="p-2 max-h-[70vh] overflow-y-auto no-scrollbar">
                  {(activeMenu === "trip" ? tripTypes : activeMenu === "vehicle" ? vehicleTypes : passengerOptions).map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => {
                        if (activeMenu === "trip") setTripType(option);
                        else if (activeMenu === "vehicle") setVehicleType(option);
                        else setPassengers(option);
                        setActiveMenu(null);
                      }}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0"
                    >
                      <span className="text-lg font-medium text-gray-800 text-left mr-3">{option}</span>
                      <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                        (activeMenu === "trip" ? tripType : activeMenu === "vehicle" ? vehicleType : passengers) === option 
                          ? "border-[#2b8a00] bg-[#2b8a00]" 
                          : "border-gray-300 bg-white"
                      }`}>
                        {(activeMenu === "trip" ? tripType : activeMenu === "vehicle" ? vehicleType : passengers) === option && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default BookingForm;
